<template>
    <div
        ref="rootRef"
        :class="notificationBoxClass"
        :style="{ opacity: isVisible ? 1 : 0 }"
        @click.stop
    >
        <!-- icon -->
        <svg-icon
            v-if="icon"
            :icon-name="icon"
            :width="iconSize"
            :height="iconSize"
            :color="iconColor"
            class="icon"
            style="flex-shrink: 0"
        ></svg-icon>

        <!-- text -->
        <div class="text">
            <p v-if="title" class="title">{{ title }}</p>
            <p class="body">{{ body }}</p>
        </div>

        <!-- close -->
        <span v-if="closable" class="close-btn" @click="closeNotification">
            <span
                v-if="direction === NotificationDirection.Center"
                :style="{ color: iconColor }"
            >
                確定
            </span>
            <svg-icon
                v-else
                icon-name="close-regular"
                :width="16"
                :height="16"
                :color="iconColor"
                style="flex-shrink: 0"
            ></svg-icon>
        </span>
    </div>
</template>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import SvgIcon from '@/components/base/SvgIcon.vue';
    import {
        NotificationDirection,
        NotificationType,
    } from '@/types/components/base-components.types';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            id: string;
            type: NotificationType;
            title: string;
            body: string;
            icon?: string;
            timeout: number;
            closable?: boolean;
            persistent?: boolean;
            stack: boolean;
            direction: NotificationDirection;
        }>(),
        {
            icon: '',
            closable: false,
            persistent: false,
            direction: NotificationDirection.Center,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'close'): void;
    }>();

    // ----- state -----
    const rootRef = ref<HTMLElement | null>(null);
    const isVisible = ref(false);
    const showTimerRef = ref<number | null>(null);
    const closeTimerRef = ref<number | null>(null);
    const autoCloseTimerRef = ref<number | null>(null);

    // ----- computed -----
    const notificationBoxClass = computed(() => {
        return [
            'notification-box',
            `notification-${props.type}`,
            props.icon ? 'has-icon' : '',
            props.title ? 'has-title' : '',
            [NotificationDirection.Center].includes(props.direction)
                ? 'is-center-layout'
                : 'is-edge-layout',
        ];
    });

    const iconSize = computed(() => {
        return props.direction === NotificationDirection.Center ? 32 : 20;
    });

    const iconColor = computed(() => {
        const colorConfig: Record<NotificationType, string> = {
            [NotificationType.Basic]: '#ffffff',
            [NotificationType.Warning]: '#f9b207',
            [NotificationType.Danger]: '#ffffff',
        };

        return colorConfig[props.type] || '#ffffff';
    });

    // ----- methods -----
    // 顯示通知
    const showNotification = (): void => {
        // 渲染後延遲調整 isVisible，確保淡入效果執行
        showTimerRef.value = window.setTimeout(() => {
            isVisible.value = true;
        }, 100);

        // 非 persistent 才會依 timeout 自動關閉
        if (!props.persistent && props.timeout) {
            autoCloseTimerRef.value = window.setTimeout(() => {
                closeNotification();
            }, props.timeout);
        }
    };

    // 關閉通知
    const closeNotification = (): void => {
        isVisible.value = false;

        // 等待淡出動畫完成後移除節點
        closeTimerRef.value = window.setTimeout(() => {
            emits('close');
        }, 300);
    };

    // 處理外部點擊事件
    const handleOutsideClick = (event: Event): void => {
        const root = rootRef.value;
        const target = event.target as Node | null;
        if (!root || !target) return;

        if (!root.contains(target)) {
            closeNotification();
        }
    };

    // 非堆疊且非常駐才允許點擊外部關閉
    const canCloseOnOutsideClick = (): boolean => {
        return !props.stack && !props.persistent;
    };

    // ----- lifecycle -----
    onMounted(() => {
        showNotification();

        if (canCloseOnOutsideClick()) {
            window.setTimeout(() => {
                document.addEventListener('click', handleOutsideClick);
            }, 100);
        }
    });

    onBeforeUnmount(() => {
        if (canCloseOnOutsideClick()) {
            document.removeEventListener('click', handleOutsideClick);
        }

        if (showTimerRef.value) {
            window.clearTimeout(showTimerRef.value);
            showTimerRef.value = null;
        }

        if (closeTimerRef.value) {
            window.clearTimeout(closeTimerRef.value);
            closeTimerRef.value = null;
        }

        if (autoCloseTimerRef.value) {
            window.clearTimeout(autoCloseTimerRef.value);
            autoCloseTimerRef.value = null;
        }
    });
</script>

<style lang="scss" scoped>
    @use 'sass:map';

    $notification-config: (
        basic: (
            background: rgba(6, 41, 75, 0.75),
            color: #ffffff,
        ),
        warning: (
            background: #fff8c6,
            color: #454545,
            border: #f9b207,
        ),
        danger: (
            background: rgba(255, 102, 102, 0.75),
            color: #ffffff,
        ),
    );

    .notification-box {
        position: relative;
        padding: 28px 16px;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
        justify-content: center;
        pointer-events: all;
        transition: opacity 0.3s ease-in-out;

        @each $key, $values in $notification-config {
            $background: map.get($values, background);
            $color: map.get($values, color);

            &.notification-#{$key} {
                background-color: $background;
                color: $color;

                @if map.has-key($values, border) {
                    border: 1px solid map.get($values, border);
                }
            }
        }

        &:not(.has-icon) {
            padding: 16px;
        }

        .close-btn {
            flex-shrink: 0;
            font-size: 16px;
            cursor: pointer;
        }

        .text {
            text-align: left;

            p {
                margin: 0;
                line-height: 1.5;
            }

            .title {
                font-size: 20px;
                font-weight: 700;
            }

            .body {
                font-size: 16px;
            }
        }

        &.is-edge-layout {
            padding: 12px;
            flex-direction: row;
            gap: 4px;
            align-items: center;

            .close-btn {
                text-align: center;
            }

            // 含 title 時改為頂端對齊，icon 微調對齊首行文字
            &.has-title {
                align-items: flex-start;

                .icon {
                    margin-top: 4px;
                }

                .close-btn {
                    margin-top: 6px;
                }
            }
        }

        &.is-center-layout {
            .text {
                text-align: center;

                .title {
                    margin-bottom: 8px;
                }

                .body {
                    text-align: start;
                }
            }
        }
    }
</style>
